import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from './PageLayout';
import ArticleList from './ArticleList';
import { getArticles, updateArticle } from '../api';
import useAuth from '../auth/useAuth';
import Button from './ui/Button';
import PageHeader from './ui/PageHeader';
import PageBody from './ui/PageBody';
import PageMain from './ui/PageMain';

function MultiSelectDropdown({
    label,
    options,
    selected,
    onChange,
    t
}) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    function toggleOption(option) {
        if (selected.includes(option)) {
            onChange(selected.filter((item) => item !== option));
        } else {
            onChange([...selected, option]);
        }
    }

    return (
        <div
            className="account-choice"
            ref={dropdownRef}
            style={{ position: 'relative' }}
        >
            <button
                type="button"
                className="ui-button account-secondary-button"
                onClick={() => setIsOpen((value) => !value)}
                aria-expanded={isOpen}
            >
                <span>
                    {selected.length > 0
                        ? `${label} (${selected.length})`
                        : label}
                </span>

                <i
                    className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`}
                    aria-hidden="true"
                />
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 5px)',
                        left: 0,
                        zIndex: 10000,
                        width: '100%',
                        minWidth: '220px',
                        maxHeight: '260px',
                        overflowY: 'auto',
                        padding: '8px',
                        boxSizing: 'border-box',
                        background: 'var(--background-color, #fff)',
                        color: 'var(--text-color, #222)',
                        border: '1px solid var(--border-color, #ccc)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                >
                    {options.length > 0 ? (
                        options.map((option) => (
                            <label
                                key={option}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '1px 5px',
                                    cursor: 'pointer',
                                    borderRadius: '5px',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(option)}
                                    onChange={() => toggleOption(option)}
                                    style={{
                                        flexShrink: 0,
                                        width: '16px',
                                        height: '16px',
                                        margin: 0,
                                    }}
                                />

                                <span>{option}</span>
                            </label>
                        ))
                    ) : (
                        <p style={{ padding: '8px', margin: 0 }}>
                            {t('articlesPage.noOptions')}
                        </p>
                    )}

                    {selected.length > 0 && (
                        <button
                            type="button"
                            className="ui-button account-secondary-button"
                            onClick={() => onChange([])}
                            style={{
                                width: '100%',
                                marginTop: '6px',
                            }}
                        >
                            {t('articlesPage.clearSelection')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function ArticlesPage() {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();

    const canManageArticles =
        user?.rightsAnywhere?.includes('articles.manage') ?? false;

    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [sortBy, setSortBy] = useState('default');
    const [sortDirection, setSortDirection] = useState('asc');

    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedAuthors, setSelectedAuthors] = useState([]);

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function loadArticles() {
            try {
                setLoading(true);
                setError('');

                const data = await getArticles(false);

                if (!cancelled) {
                    setArticles(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error('Failed to load articles:', err);

                if (!cancelled) {
                    setError(t('articlesPage.loadError'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadArticles();

        return () => {
            cancelled = true;
        };
    }, [t]);

    const authors = useMemo(() => {
        return [
            ...new Set(
                articles
                    .map(
                        (article) =>
                            article.author?.name ||
                            article.author?.nickname
                    )
                    .filter(Boolean)
            ),
        ].sort((a, b) => a.localeCompare(b, i18n.language));
    }, [articles, i18n.language]);

    const tags = useMemo(() => {
        return [
            ...new Set(
                articles
                    .flatMap((article) =>
                        (article.tags || []).map((tag) =>
                            typeof tag === 'string' ? tag : tag?.name
                        )
                    )
                    .filter(Boolean)
            ),
        ].sort((a, b) => a.localeCompare(b, i18n.language));
    }, [articles, i18n.language]);

    function getArticleTimestamp(article) {
        const timestamp = Number(article.creation_date);

        return Number.isFinite(timestamp) ? timestamp : 0;
    }

    const filteredArticles = useMemo(() => {
        return articles.filter((article) => {
            const articleTags = (article.tags || [])
                .map((tag) =>
                    typeof tag === 'string' ? tag : tag?.name
                )
                .filter(Boolean);

            if (
                selectedTags.length > 0 &&
                !selectedTags.some((tag) => articleTags.includes(tag))
            ) {
                return false;
            }

            const author =
                article.author?.name ||
                article.author?.nickname ||
                '';

            if (
                selectedAuthors.length > 0 &&
                !selectedAuthors.includes(author)
            ) {
                return false;
            }

            const timestamp = getArticleTimestamp(article);

            if (!timestamp && (dateFrom || dateTo)) {
                return false;
            }

            const articleDate = timestamp
                ? new Date(timestamp * 1000)
                : null;

            if (dateFrom) {
                const from = new Date(`${dateFrom}T00:00:00`);

                if (!articleDate || articleDate < from) {
                    return false;
                }
            }

            if (dateTo) {
                const to = new Date(`${dateTo}T23:59:59.999`);

                if (!articleDate || articleDate > to) {
                    return false;
                }
            }

            return true;
        });
    }, [
        articles,
        selectedTags,
        selectedAuthors,
        dateFrom,
        dateTo,
    ]);

    const displayedArticles = useMemo(() => {
        const result = [...filteredArticles];

        if (sortBy === 'title') {
            result.sort((a, b) =>
                (a.title || '').localeCompare(
                    b.title || '',
                    i18n.language,
                    {
                        numeric: true,
                        sensitivity: 'base',
                    }
                )
            );
        } else if (sortBy === 'author') {
            result.sort((a, b) => {
                const authorA =
                    a.author?.name ||
                    a.author?.nickname ||
                    '';

                const authorB =
                    b.author?.name ||
                    b.author?.nickname ||
                    '';

                return authorA.localeCompare(
                    authorB,
                    i18n.language
                );
            });
        } else if (sortBy === 'date') {
            result.sort((a, b) =>
                getArticleTimestamp(a) - getArticleTimestamp(b)
            );
        }

        if (sortBy !== 'default' && sortDirection === 'desc') {
            result.reverse();
        }

        return result;
    }, [
        filteredArticles,
        sortBy,
        sortDirection,
        i18n.language,
    ]);

    function resetFilters() {
        setSortBy('default');
        setSortDirection('asc');
        setSelectedTags([]);
        setSelectedAuthors([]);
        setDateFrom('');
        setDateTo('');
    }

    async function handleToggleVisibility(article) {
        try {
            const updatedArticle = await updateArticle(
                article.article_id,
                {
                    is_hidden: !article.is_hidden,
                }
            );

            setArticles((currentArticles) =>
                currentArticles.map((item) =>
                    item.article_id === article.article_id
                        ? {
                            ...item,
                            is_hidden: updatedArticle.is_hidden,
                        }
                        : item
                )
            );
        } catch (err) {
            console.error(
                'Failed to change article visibility:',
                err
            );

            setError(t('articlesPage.visibilityError'));
        }
    }

    function openDatePicker(id) {
        const input = document.getElementById(id);

        if (input?.showPicker) {
            input.showPicker();
        } else {
            input?.focus();
        }
    }

    return (
        <PageLayout showSearch showAiSearch>
            <PageMain className="articles-page-main">
                <PageHeader
                    title={t('articlesPage.title')}
                    condensed
                    note={
                        !loading &&
                            !error &&
                            articles.length === 0
                            ? t('common.emptyNote')
                            : null
                    }
                    actions={
                        canManageArticles && (
                            <Button
                                to="/create-article"
                                variant="primary"
                            >
                                <i
                                    className="fa-solid fa-plus"
                                    aria-hidden="true"
                                />
                                {t('common.createContent')}
                            </Button>
                        )
                    }
                />

                {!loading && !error && articles.length > 0 && (
                    <div className="article-controls article-controls--articles-page">
                        <div className="article-controls__top">
                            <div className="account-choice article-sort-wrapper">
                                <button
                                    type="button"
                                    className="ui-button account-secondary-button"
                                    onClick={() => document.getElementById('article-sort-select').click()}
                                >
                                    <span>
                                        {sortBy === 'default' ? t('articlesPage.sortDefault') :
                                            sortBy === 'title' ? t('articlesPage.sortTitle') :
                                                sortBy === 'author' ? t('articlesPage.filterByAuthor') :
                                                    t('articlesPage.sortDate')}
                                    </span>
                                    <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                                </button>

                                <select
                                    id="article-sort-select"
                                    value={sortBy}
                                    onChange={(event) => setSortBy(event.target.value)}
                                    aria-label={t('articlesPage.sortLabel')}
                                >
                                    <option value="default">{t('articlesPage.sortDefault')}</option>
                                    <option value="title">{t('articlesPage.sortTitle')}</option>
                                    <option value="date">{t('articlesPage.sortDate')}</option>
                                </select>
                            </div>
                            <button
                                type="button"
                                className="ui-button account-secondary-button"
                                onClick={() =>
                                    setSortDirection((direction) =>
                                        direction === 'asc' ? 'desc' : 'asc'
                                    )
                                }
                                aria-label={t(
                                    sortDirection === 'asc'
                                        ? 'articlesPage.sortAscending'
                                        : 'articlesPage.sortDescending'
                                )}
                                disabled={sortBy === 'default'}
                            >
                                {sortDirection === 'asc' ? '↑' : '↓'}
                            </button>

                            <button
                                type="button"
                                className="ui-button account-secondary-button"
                                onClick={() =>
                                    setShowFilters((value) => !value)
                                }
                                aria-expanded={showFilters}
                            >
                                <i
                                    className="fa-solid fa-filter"
                                    aria-hidden="true"
                                />

                                {showFilters
                                    ? t('articlesPage.hideFilters')
                                    : t('articlesPage.showFilters')}
                            </button>

                            <button
                                type="button"
                                className="ui-button account-secondary-button"
                                onClick={resetFilters}
                            >
                                <i
                                    className="fa-solid fa-rotate-left"
                                    aria-hidden="true"
                                />

                                {t('articlesPage.resetFilters')}
                            </button>
                        </div>

                        {showFilters && (
                            <div
                                className="article-controls__filters"
                                style={{ overflow: 'visible' }}
                            >
                                <MultiSelectDropdown
                                    label={t('articlesPage.filterByTag')}
                                    options={tags}
                                    selected={selectedTags}
                                    onChange={setSelectedTags}
                                    t={t}
                                />

                                <MultiSelectDropdown
                                    label={t('articlesPage.filterByAuthor')}
                                    options={authors}
                                    selected={selectedAuthors}
                                    onChange={setSelectedAuthors}
                                    t={t}
                                />

                                <div className="ui-button account-secondary-button">
                                    <label htmlFor="article-date-from">
                                        {t('articlesPage.dateFrom')}
                                    </label>

                                    <div className="article-date-filter__control">
                                        <input
                                            id="article-date-from"
                                            type="date"
                                            value={dateFrom}
                                            onChange={(event) =>
                                                setDateFrom(event.target.value)
                                            }
                                            className="article-date-filter__input"
                                        />

                                        <button
                                            type="button"
                                            className="article-date-filter__button"
                                            onClick={() =>
                                                openDatePicker('article-date-from')
                                            }
                                            aria-label={t('articlesPage.dateFrom')}
                                        >
                                            <i
                                                className="fa-regular fa-calendar"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <div className="ui-button account-secondary-button">
                                    <label htmlFor="article-date-to">
                                        {t('articlesPage.dateTo')}
                                    </label>

                                    <div className="article-date-filter__control">
                                        <input
                                            id="article-date-to"
                                            type="date"
                                            value={dateTo}
                                            min={dateFrom || undefined}
                                            onChange={(event) =>
                                                setDateTo(event.target.value)
                                            }
                                            className="article-date-filter__input"
                                        />

                                        <button
                                            type="button"
                                            className="article-date-filter__button"
                                            onClick={() =>
                                                openDatePicker('article-date-to')
                                            }
                                            aria-label={t('articlesPage.dateTo')}
                                        >
                                            <i
                                                className="fa-regular fa-calendar"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {loading ? (
                    <PageBody>
                        <p className="page-message">
                            {t('common.loading')}
                        </p>
                    </PageBody>
                ) : error ? (
                    <PageBody>
                        <p className="page-message">{error}</p>
                    </PageBody>
                ) : articles.length === 0 ? null : displayedArticles.length === 0 ? (
                    <PageBody>
                        <p className="page-message">
                            {t('articlesPage.noMatchingArticles')}
                        </p>
                    </PageBody>
                ) : (
                    <ArticleList
                        articles={displayedArticles}
                        onToggleVisibility={handleToggleVisibility}
                    />
                )}
            </PageMain>
        </PageLayout>
    );
}

export default ArticlesPage;
